import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ResponsiveNavbar from '../components/shared/ResponsiveNavbar';
import Footer from '../components/shared/Footer';
import { publicationsAPI } from '../services/api';
import './Publications.css';

const Publications = () => {
  const [publications, setPublications] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const loadLibrary = async () => {
      try {
        setLoading(true);
        setError('');
        const [publicationResponse, categoryResponse] = await Promise.all([
          publicationsAPI.getPublic(),
          publicationsAPI.getCategories()
        ]);
        setPublications(publicationResponse.data || []);
        setCategories(categoryResponse.data || []);
      } catch (loadError) {
        console.error('Failed to load publications:', loadError);
        setError(loadError.message || 'The publication library could not be loaded.');
      } finally {
        setLoading(false);
      }
    };
    loadLibrary();
  }, []);

  const visiblePublications = useMemo(
    () => selectedCategory
      ? publications.filter((publication) => publication.category_slug === selectedCategory)
      : publications,
    [publications, selectedCategory]
  );

  const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="publications-page-shell">
      <ResponsiveNavbar logo="VELOURA" logoLink="/" scrolled={scrolled} />

      <main className="publications-page">
        <header className="publications-hero">
          <span className="publications-eyebrow">Veloura Editorial</span>
          <h1>Publications</h1>
          <p>Magazines, features, and official releases from Veloura.</p>
        </header>

        <section className="publications-library" aria-labelledby="publication-library-title">
          <div className="publications-library-heading">
            <div>
              <span className="publications-section-rule" aria-hidden="true" />
              <h2 id="publication-library-title">The Library</h2>
            </div>
            {!loading && !error && (
              <span className="publications-count">
                {visiblePublications.length} {visiblePublications.length === 1 ? 'release' : 'releases'}
              </span>
            )}
          </div>

          <div className="publications-filters" role="tablist" aria-label="Filter publications">
            <button
              type="button"
              className={!selectedCategory ? 'active' : ''}
              onClick={() => setSelectedCategory('')}
            >
              All
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                className={selectedCategory === category.slug ? 'active' : ''}
                onClick={() => setSelectedCategory(category.slug)}
              >
                {category.name}
                <span>{category.publication_count}</span>
              </button>
            ))}
          </div>

          {loading && (
            <div className="publications-state" aria-live="polite">
              <span className="publications-state-rule" />
              <p>Opening the library...</p>
            </div>
          )}

          {!loading && error && (
            <div className="publications-state error" role="alert">
              <span className="publications-state-rule" />
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && visiblePublications.length === 0 && (
            <div className="publications-state">
              <span className="publications-state-rule" />
              <p>No publications are available in this collection yet.</p>
            </div>
          )}

          {!loading && !error && visiblePublications.length > 0 && (
            <div className="publications-grid">
              {visiblePublications.map((publication) => (
                <Link
                  className="publication-card"
                  to={`/publication/${publication.slug}`}
                  key={publication.id}
                >
                  <div className="publication-cover">
                    {publication.cover_image_url ? (
                      <img
                        src={publication.cover_image_url}
                        alt={`Cover of ${publication.title}`}
                        loading="lazy"
                      />
                    ) : (
                      <div className="publication-cover-fallback" aria-hidden="true">
                        <span>Veloura</span>
                        <strong>{publication.issue_label || 'Publication'}</strong>
                      </div>
                    )}
                    <span className="publication-open-label">Read now</span>
                  </div>
                  <div className="publication-card-copy">
                    <div className="publication-card-meta">
                      <span>{publication.category_name || 'Publication'}</span>
                      <span>{formatDate(publication.published_at || publication.created_at)}</span>
                    </div>
                    <h3>{publication.title}</h3>
                    {(publication.subtitle || publication.issue_label) && (
                      <p>{publication.subtitle || publication.issue_label}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Publications;
